// Platform-console i18n namespace (spec 15 §4). Wired into apps/web's
// aggregated `dictionaries` (src/i18n-dictionaries.ts) so the CI key-parity
// gate (`pnpm run i18n:parity`) covers it automatically.
//
// `adminEn` is authoritative; `AdminDict` widens its values to `string`
// (src/i18n-shape.ts) so every locale stays parity-checked at COMPILE time while
// carrying real translations. ru IS TRANSLATED (admin.ru.ts) — the console's
// only user today reads English, but the locale switcher is on the screen and a
// half-translated area is exactly the defect i18n-completeness.ts exists to
// stop. fr, zh, zh-Hant and hi are machine-translated in admin.<locale>.ts,
// which src/locales/<locale>.ts imports directly.
//
// NO USER-FACING STRING IS HARDCODED IN THIS AREA (the i18n rule).

import type { Translated } from "../../i18n-shape.js";
import { adminRuDict } from "./admin.ru.js";
import { adminOpsEn } from "./admin-ops.i18n.js";
import { adminAgentsEn } from "./admin-agents.i18n.js";

export const adminEn = {
  brand: "Assessly",
  console: "Platform console",

  notFound: {
    title: "That console page does not exist",
    body: "Check the address, or go back to sign in.",
    cta: "Go to sign in",
  },

  // M15-4 (spec 15 §4 S9, S15; D-611 to D-613): tenant health and the logs.
  health: {
    nav: "Health",
    title: "Tenant health",
    lede: "Every active tenant, worst first, from the latest window.",
    windowMeta: "Window {{time}}",
    loading: "Loading health…",
    none: "—",
    open: "Open health",
    colTenant: "Tenant",
    colHealth: "Health",
    colUptime: "Uptime 24 h",
    colErrors: "Error rate",
    colP95: "p95 latency",
    colLag: "Event lag",
    colParked: "Parked events",
    colAlerts: "Open alerts",
    colKind: "Alert",
    colOpened: "Opened",
    colState: "State",
    colPaged: "Paged",
    alertKind: {
      canary_down: "Not answering",
      queue_lag: "Events behind",
      backup_stale: "Backup overdue",
      fleet_collapse: "Fleet outage",
    },
    dot: {
      green: "Healthy",
      amber: "Degraded",
      red: "Down",
      grey: "No data",
    },
    reasons: {
      down: "Did not answer the probe",
      alert_open: "An alert is open",
      error_rate: "Error rate at 1% or more",
      latency: "p95 latency at 2 s or more",
      queue_lag: "Events a minute or more behind",
      parked_events: "Some events are parked",
      stale: "No reading in the last 15 minutes",
      not_probed: "Not active, so not probed",
      health_drill: "A health drill is on",
    },
    noWindowTitle: "No readings yet",
    noWindowBody: "The collector writes its first window within ten minutes of starting.",
    stale:
      "The latest window is over 15 minutes old. Until the collector runs again, only tenants with an open alert stay red.",
    statProbed: "Tenants probed",
    statDown: "Down now",
    statAlerts: "Open alerts",
    statBackup: "Last backup",
    backupOff: "Not watched here",
    backupNone: "None found",
    backupStale: "Older than 26 hours",
    tableTitle: "Active tenants",
    emptyTitle: "No active tenants",
    emptyBody: "A tenant appears here once its setup finishes.",
    tenantTitle: "{{name}} health",
    drillOn: "A health drill is on: this tenant answers its probe as down on purpose.",
    now: "Latest window",
    noReadingTitle: "No reading for this tenant yet",
    noReadingBody: "Readings start with the first window after the tenant becomes active.",
    probe: "Probe",
    probeUp: "Answered in {{ms}}",
    probeDown: "Did not answer",
    requests: "Requests in the window",
    noTraffic: "No traffic",
    last24h: "Last 24 hours",
    stripUp: "Reachable",
    up: "Answered",
    down: "Did not answer",
    notCollected: "Not collected",
    stripSummary: "Bad: {{bad}} · warning: {{warn}} · no data: {{none}} · windows: {{total}}",
    last30d: "Last 30 days",
    noDays: "No full day has been rolled up yet.",
    uptime: "Uptime",
    daysSummary: "Days with data: {{count}}",
    alertsTitle: "Alerts",
    viewLogs: "View logs",
    noAlerts: "No alerts for this tenant.",
    alertOpen: "Open",
    alertResolved: "Resolved {{time}}",
    paged: "Paged",
    held: "Held, not paged",
    drillTitle: "Health drill",
    drillBody:
      "A drill makes this sandbox tenant answer its probe as down, so the board and alerts can be checked. It never pages.",
    drillFailed: "The drill did not change. Try again.",
    drillStart: "Start health drill",
    drillStop: "Stop health drill",
    drillStartBody:
      "The tenant will read as down from the next window, and its alerts will open without paging.",
    drillStopBody: "The tenant will answer its probe again from the next window.",
    cancel: "Cancel",
    saving: "Saving…",
    confirm: "Confirm",
  },
  logs: {
    nav: "Logs",
    title: "Logs",
    lede: "Server errors and health alerts, newest first.",
    loading: "Loading logs…",
    colTime: "Time",
    colTenant: "Tenant",
    colStatus: "Status",
    colRoute: "Request",
    colCode: "Problem code",
    colDuration: "Duration",
    colRequest: "Request ID",
    host: {
      tenant: "Tenant",
      admin: "Console",
      other: "Other host",
    },
    fleet: "Whole fleet",
    errors: "Server errors",
    alerts: "Health alerts",
    noErrorsTitle: "No server errors",
    noErrorsBody: "Nothing answered with a server error in this period.",
    noAlertsTitle: "No alerts",
    noAlertsBody: "The collector has not opened an alert that matches.",
    oneTenant: "One tenant",
    allTenants: "Show every tenant",
    since: "Period",
    since1h: "Last hour",
    since24h: "Last 24 hours",
    since7d: "Last 7 days",
    state: "Alerts",
    stateAll: "Open and resolved",
    stateOpen: "Open only",
  },

  // Plans, entitlements, usage and tenant edits (spec 15 §4 S5/S6/S7/S11/S12;
  // M15-6, M15-7a).
  tenantTabs: {
    label: "Tenant sections",
    overview: "Overview",
    entitlements: "Entitlements",
    usage: "Usage",
  },
  createPlan: {
    label: "Plan",
    loading: "Loading plans…",
    failed: "Plans could not be loaded",
    none: "No live plan includes this industry pack.",
    refused: "The chosen plan cannot be used for this tenant. Pick another plan.",
  },
  plans: {
    nav: "Plans",
    title: "Plans",
    create: "New plan",
    colName: "Plan",
    colCode: "Code",
    colStatus: "Status",
    colPrice: "Annual price",
    colTenants: "Tenants",
    colModules: "Modules",
    colActions: "Actions",
    priceHidden: "Hidden for your role",
    sandbox: "Sandbox",
    showRetired: "Show retired plans",
    loading: "Loading plans…",
    emptyTitle: "No plans to show",
    emptyBody: "Plans set which modules and limits a tenant gets.",
    emptyBodyAdmin: "Create a plan to set which modules and limits a tenant gets.",
    edit: "Edit",
    retire: "Retire",
    retireTitle: "Retire {{name}}?",
    retireBody: "Tenants already on this plan keep it. No new tenant can be given it.",
    retireConfirm: "Retire plan",
    retiring: "Retiring…",
    editorCreate: "New plan",
    editorEdit: "Edit {{name}}",
    code: "Code",
    codeHint: "Lowercase letters, digits and hyphens. The code is fixed once saved.",
    name: "Name",
    price: "Annual price",
    currency: "Currency",
    dunningDays: "Suspend after days unpaid",
    notes: "Notes",
    modules: "Modules",
    limits: "Limits",
    limitValue: "Limit",
    warnPct: "Warn at %",
    blockPct: "Block at %",
    affects: "Tenants on this plan: {{count}}. Every one of them gets this change.",
    save: "Save plan",
    saving: "Saving…",
    cancel: "Cancel",
    close: "Close",
    failed: "The plan could not be saved. Try again.",
    codeTaken: "Another plan already uses this code.",
    thresholds: "Warn must be below block, and both from 1 to 100.",
    retiredEdit: "A retired plan cannot be edited.",
    pilotRequired: "Pilot cannot be retired: every tenant without a plan of its own is on it.",
    refusedScope: "Your access does not cover this plan.",
    status: {
      active: "Active",
      grandfathered: "Grandfathered",
      retired: "Retired",
    },
  },
  moduleCodes: {
    catalogue: "Catalogue",
    onboarding: "Onboarding",
    inventory: "Inventory",
    waste: "Waste",
    production: "Production",
    quality: "Quality",
    planning: "Planning",
    sales: "Sales and CRM",
    messaging: "Messaging",
    agents: "AI agents",
  },
  limitCodes: {
    max_facilities: "Facilities",
    max_users_soft: "Users",
    storage_gb: "Storage (GB)",
    api_requests_day: "API requests per day",
  },
  enforcement: {
    hard: "Hard limit",
    soft: "Soft limit",
    fair_use: "Fair use",
    soft_then_hard: "Warn, then block",
  },
  entitlements: {
    packCode: "Industry pack: {{pack}}",
    loading: "Loading entitlements…",
    plan: "Plan",
    changePlan: "Change plan",
    modules: "Modules",
    limits: "Limits",
    moduleCol: "Module",
    planCol: "Plan",
    overrideCol: "Override",
    effectiveCol: "Effective",
    actionsCol: "Actions",
    limitCol: "Limit",
    usageCol: "Usage",
    on: "On",
    off: "Off",
    none: "None",
    noLimit: "No limit",
    noUsage: "Not measured",
    usedOf: "{{used}} of {{limit}}",
    until: "Until {{date}}",
    noExpiry: "No end date",
    overrideAdd: "Override",
    overrideEdit: "Edit override",
    overrideTitle: "Override: {{name}}",
    switchOn: "Turn on for this tenant",
    value: "Limit value",
    reason: "Reason",
    reasonHint: "At least 5 characters. Kept in the audit log.",
    expires: "Ends on",
    expiresHint: "Leave empty to keep the override until it is removed.",
    invalid: "Check the value and the end date.",
    failed: "The override could not be saved. Try again.",
    save: "Save override",
    remove: "Remove override",
    removing: "Removing…",
    planDialogTitle: "Change the plan for {{name}}",
    planSelect: "New plan",
    planPick: "Choose a plan",
    noOtherPlan: "No other plan fits this tenant's industry pack.",
    belowUsage: "The new facility limit, {{limit}}, is below current use, {{usage}}.",
    force: "Move anyway. The tenant keeps what it already has.",
    belowUsageRefused: "The new plan's limits are below this tenant's current use.",
    sandboxOnly: "Sandbox plans are only for sandbox tenants.",
    packNotInPlan: "That plan does not include this tenant's industry pack.",
    planFailed: "The plan could not be changed. Try again.",
  },
  usage: {
    loading: "Loading usage…",
    month: "Month",
    monthTitle: "Month totals",
    noRollup: "No totals for this month.",
    mau: "Monthly active users",
    domainEvents: "Domain events",
    storagePeak: "Peak storage",
    refreshed: "Updated",
    daily: "Last 90 days",
    exportCsv: "Download CSV",
    csvDate: "Date",
    metric: {
      active_users: "Active users",
      domain_events: "Domain events",
      storage_bytes: "Storage",
    },
    sparkLabel: "{{metric}} per day, {{from}} to {{to}}",
    noReading: "No reading",
    window: "Daily readings from {{from}} to {{to}}. A blank day was not measured.",
    emptyTitle: "No usage measured",
    emptyBody: "Usage is measured every hour. Readings show here after the first pass.",
  },
  tenantEdit: {
    edit: "Edit details",
    subdomain: "Change subdomain",
    plan: "Plan",
    colPlan: "Plan",
    colMau: "MAU",
    title: "Edit {{name}}",
    name: "Name",
    locale: "Default language",
    timezone: "Time zone",
    currencyLocked: "The currency cannot be changed here.",
    failed: "The changes could not be saved. Try again.",
    save: "Save changes",
    subdomainTitle: "Change the subdomain of {{name}}",
    newSubdomain: "New subdomain",
    subdomainPreview: "New address: {{host}}",
    subdomainRule:
      "3 to 63 lowercase letters, digits or hyphens, starting and ending with a letter or digit.",
    reason: "Reason",
    subdomainHint:
      "Tell the tenant first: their users sign in at the new address after the change.",
    subdomainTaken: "Another tenant uses this subdomain.",
    subdomainReserved: "This subdomain is reserved.",
    subdomainFailed: "The subdomain could not be changed. Try again.",
    subdomainSave: "Change subdomain",
  },

  // The §3.1.6 router guard: GET /v1/platform/me must answer 200 before any
  // signed-in screen renders.
  guard: {
    checking: "Checking your session…",
    errorTitle: "Could not reach the platform console",
    errorBody: "The session check failed. This is usually a network problem — try again.",
    retry: "Try again",
  },

  accept: {
    title: "Set your password",
    lead: "Choose a password, then set up two-factor sign-in.",
    password: "New password",
    passwordHint: "At least 12 characters. A passphrase of a few unrelated words is strongest.",
    confirm: "Repeat password",
    show: "Show password",
    hide: "Hide password",
    submit: "Set password",
    submitting: "Setting your password…",
    mismatch: "The two passwords are not the same.",
    tooShort: "Use at least 12 characters.",
    // ONE message for every token failure — the API deliberately does not say
    // whether a token expired, was used, or never existed, and neither does
    // this screen (spec 15 §0.2).
    invalidToken: "This invite link is no longer usable. Ask for a new invite.",
    missingToken: "This page needs the invite link from your email — open the link itself.",
    missingTokenHelp:
      "The link is incomplete, often from copying it by hand. Ask for a new invite, or sign in if your password is set.",
    goSignIn: "Go to sign in",
    weak: "That password is too easy to guess. Try a longer passphrase.",
    failed: "Could not set the password. Try again.",
    requestId: "Request id",
    strengthLabel: "Password strength",
    strength: {
      none: "—",
      weak: "Weak",
      fair: "Fair",
      good: "Good",
      strong: "Strong",
    },
    strengthNote: "A guide only — the server checks the password itself.",
  },

  login: {
    title: "Sign in",
    lead: "Assessly staff only. Every account needs two-factor authentication.",
    passwordSet: "Password set. Sign in to finish setting up two-factor authentication.",
    // Flash from /console or /enroll when a write came back 401 with no TOTP
    // verdict: the SESSION died mid-action. The sentence names the session so
    // the reader does not re-litigate a code that was never judged.
    sessionExpired: "Your session has expired. Sign in again to continue.",
    email: "Email",
    password: "Password",
    submit: "Sign in",
    submitting: "Signing in…",
    // No oracle: the same sentence whether the address exists or not.
    failed: "That email and password did not match. Try again.",
    locked:
      "Too many failed attempts. This account is locked for at least 15 minutes — wait, then try again.",
    error: "Could not sign in. Try again.",
    throttled: "Too many sign-in attempts. Try again {{wait}}.",
    throttledLater: "Too many sign-in attempts. Wait a minute, then try again.",
    totpTitle: "Two-factor code",
    totpLead: "Enter the 6-digit code from your authenticator app.",
    // The lead when the recovery-code mode is on — the screen must not still be
    // asking for an authenticator code while the field says "Recovery code".
    recoveryLead: "Enter one of the recovery codes you saved. Each one works once.",
    code: "Authentication code",
    codeHint: "Six digits, from the app you set up when you enrolled.",
    codeFormat: "An authenticator code is exactly six digits.",
    verify: "Verify",
    verifying: "Verifying…",
    // Shown back on the EMAIL + PASSWORD step, because the server consumes the
    // sign-in ticket on the first attempt whatever the verdict — so the honest
    // instruction after a miss is "start again", not "try another code".
    totpFailed:
      "That code wasn't accepted, and this step allows one attempt. Enter your email and password again, then use your app's current code.",
    // The same event in recovery-code mode. It must NOT talk about codes that
    // change every 30 seconds: the person reading it has lost the phone that
    // produces those, which is why they are here.
    recoveryFailed:
      "That recovery code wasn't accepted. Each code works once. Enter your email and password, then try another code from your list.",
    recoveryFormat: "A recovery code looks like ABCDE-FGHJK — ten characters and a dash.",
    useRecovery: "Use a recovery code instead",
    useCode: "Use an authenticator code instead",
    recoveryCode: "Recovery code",
    recoveryHint: "One of the ten codes you saved when you set up two-factor authentication.",
    expired: "That sign-in step timed out. Enter your email and password again.",
    forgot: "Forgot password?",
    passwordReset: "Password changed. Sign in with it and your two-factor code.",
  },

  forgot: {
    title: "Reset your password",
    lead: "Enter your staff email. If it belongs to a console account, we send a reset link.",
    email: "Email",
    submit: "Send reset link",
    submitting: "Sending…",
    sentTitle: "Check your email",
    sent: "If that address has a console account, a link to set a new password is on its way. It works once, for 30 minutes.",
    sentNote: "If you never finished your invite, the invite comes again instead.",
    error: "Could not send the request. Try again.",
    backToSignIn: "Back to sign in",
  },

  reset: {
    title: "Set a new password",
    lead: "Choose a new password. You still sign in with your two-factor code afterwards.",
    password: "New password",
    passwordHint: "At least 12 characters. A passphrase of a few unrelated words is strongest.",
    confirm: "Repeat password",
    show: "Show password",
    hide: "Hide password",
    submit: "Set new password",
    submitting: "Setting your password…",
    mismatch: "The two passwords are not the same.",
    tooShort: "Use at least 12 characters.",
    invalid: "This reset link is no longer usable.",
    invalidHelp:
      "A link works once, for 30 minutes, and a newer link replaces it. Ask for a new one.",
    requestNew: "Ask for a new link",
    missingToken: "This page needs the reset link from your email. Open the link itself.",
    weak: "That password is too easy to guess. Try a longer passphrase.",
    failed: "Could not set the password. Try again.",
    requestId: "Request id",
    goSignIn: "Go to sign in",
  },

  enroll: {
    title: "Set up two-factor authentication",
    lead: "Scan this with your authenticator app, then type its code.",
    loading: "Preparing your setup code…",
    errorTitle: "Could not start two-factor setup",
    errorBody: "The setup code could not be generated. Try again.",
    retry: "Try again",
    qrAlt: "QR code for your authenticator app",
    qrUnavailable:
      "This code is too long to draw here. Add the secret below to your app by hand instead.",
    secretLabel: "Or enter this secret by hand",
    secretHelp: "Type it into your authenticator app as a time-based (TOTP) key.",
    copySecret: "Copy secret",
    copied: "Copied",
    code: "6-digit code",
    confirm: "Confirm",
    confirming: "Confirming…",
    failed: "That code was not accepted. Wait for the next one and try again.",
    // The two sentences below exist because `failed` is a VERDICT — the server
    // comparing the digits to the enrolled secret — and these are the failures
    // where no verdict exists (probed 2026-08-17: a dead network, a 500 and an
    // expired session all rendered as `failed`, pushing the operator at the
    // retry that mints a fresh secret and kills the QR they already scanned).
    // Neither may ever claim the code was refused.
    notChecked:
      "That code could not be checked. It wasn't rejected and your authenticator setup is fine: try the same code, or the next one.",
    sessionExpired:
      "Your session timed out before the code could be checked — the code was not rejected. Sign in again to continue the setup.",
    requestId: "Request id",
    // The secret never leaves the browser: it is neither logged nor stored.
    privacy: "This secret stays in your browser and in your authenticator app.",
  },

  recovery: {
    title: "Save your recovery codes",
    warningTitle: "These are shown once and never again.",
    warningBody:
      "Each code signs you in once if you lose your phone. Save them now: we can't show them again.",
    listLabel: "Ten single-use recovery codes",
    copy: "Copy codes",
    copied: "Copied",
    download: "Download as a text file",
    fileName: "assessly-platform-recovery-codes.txt",
    fileHeader: "Assessly platform console — single-use recovery codes",
    ack: "I have saved these codes somewhere I can reach without my phone.",
    ackRequired: "Confirm you have saved the codes before continuing.",
    continue: "Continue to the console",
  },

  // The on-demand reissue on /console (§5 POST auth/recovery-codes/regenerate).
  reissue: {
    title: "Recovery codes",
    body: "Lost your recovery codes? Issue a new set now. It cancels the old ones and needs your authenticator app.",
    start: "Issue new recovery codes",
    code: "Authenticator code",
    codeHint: "Six digits from your app, to prove this is you.",
    confirm: "Issue new codes",
    working: "Issuing…",
    cancel: "Cancel",
    // Same no-oracle rule as the enrolment screen: wrong, stale and replayed
    // all read identically.
    failed: "That code was not accepted. Wait for the next one and try again.",
    format: "An authenticator code is exactly six digits.",
    // No-verdict failures (probed 2026-08-17 — see the enrolment section's
    // twin keys): these never claim the code was refused, and `notChecked`
    // deliberately claims NOTHING about the old batch — a 5xx after commit is
    // a batch rotated invisibly, so "your saved codes still work" would be a
    // guess dressed as a fact.
    notChecked:
      "That code could not be checked, but it wasn't rejected. Try the same code, or the next one your app shows.",
    sessionExpired: "Your session has expired, so the code was never checked. Sign in again.",
    requestId: "Request id",
    doneTitle: "Your new recovery codes",
    doneLead: "These replace every code issued before. The old ones no longer work.",
    back: "Back to your account",
  },

  home: {
    title: "Your account",
    lead: "Your sign-in, role and recovery codes.",
    email: "Email",
    role: "Role",
    staffId: "Staff id",
    session: "Session",
    // Facts of the guard chain (spec 15 §2.13), not a live reading — labelled
    // as the policy it is, because inventing a countdown from data the API does
    // not return would be a fabricated tile (D-033).
    sessionPolicy: "Signs you out after 60 minutes idle, or 12 hours in total.",
    twoFactor: "Two-factor",
    twoFactorOn: "Confirmed",
    twoFactorOff: "Not set up",
    signOut: "Sign out",
    signingOut: "Signing out…",
    signOutFailed: "Could not sign out cleanly. Close this browser to be sure.",
  },

  nav: {
    overview: "Overview",
    label: "Console",
    tenants: "All tenants",
    account: "Your account",
  },
  // The console shell and S3, the fleet overview (D-625, D-626).
  navGroups: {
    fleet: "Fleet",
    tenants: "Tenants",
    support: "Support",
    health: "Health",
    usage: "Usage & plans",
    staff: "Staff & audit",
    account: "Account",
  },
  shell: {
    menu: "Menu",
    env: {
      production: "Production",
      staging: "Staging",
      local: "Local",
    },
    sandboxScope: "sandbox",
    notFoundBody: "Check the address, or go back to the overview.",
    goHome: "Go to the overview",
  },
  account: {
    identity: "Identity",
  },
  fleet: {
    title: "Fleet overview",
    refresh: "Refresh",
    refreshing: "Refreshing…",
    updated: "Updated {{when}}",
    loading: "Loading the fleet…",
    partial: "Some figures could not be read. Refresh to try again.",
    attentionTitle: "Needs attention",
    attentionEmpty: "Nothing needs attention.",
    attentionMore: "Showing {{shown}} of {{total}}.",
    recentTitle: "Recent changes",
    recentEmpty: "No tenant has changed state yet.",
    since: "Since {{when}}",
    tiles: {
      tenants_active: "Active tenants",
      tenants_provisioning: "Setting up",
      tenants_suspended: "Suspended",
      tenants_provision_failed: "Setup failed",
      staff_signed_in: "Staff signed in",
    },
    attention: {
      provision_failed: "Setup failed",
      provisioning_stalled: "Setup stalled",
    },
  },

  status: {
    active: "Active",
    provisioning: "Setting up",
    provision_failed: "Setup failed",
    suspended: "Suspended",
    pending_deletion: "Deletion scheduled",
    exporting: "Exporting",
    purging: "Deleting",
    purged: "Deleted",
  },

  reasons: {
    billing_overdue: "Billing overdue",
    compliance: "Compliance",
    tenant_request: "Customer asked",
    security_incident: "Security incident",
  },

  modes: {
    grace: "Read-only for 7 days",
    immediate: "Lock now",
  },

  regions: {
    "ap-southeast-1": "Singapore",
    "eu-central-1": "Frankfurt",
    "me-central-1": "UAE",
    "af-south-1": "Cape Town",
  },

  steps: {
    seed_baseline: "Create the workspace",
    pack_seed: "Load the industry pack",
    dns_check: "Check the web address",
    invite_admin: "Invite the admin",
    activate: "Switch it on",
  },

  stepState: {
    done: "Done",
    skipped: "Skipped",
    pending: "Waiting",
  },

  // Shared request-state copy for the tenant screens.
  load: {
    failed: "Could not load this.",
    retry: "Try again",
    requestId: "Request id",
    forbidden: "Your role cannot see this.",
  },

  tenants: {
    title: "Tenants",
    create: "New tenant",
    search: "Search",
    searchPlaceholder: "Name or subdomain",
    status: "Status",
    allStatuses: "All",
    loading: "Loading tenants…",
    colName: "Name",
    colSubdomain: "Subdomain",
    colStatus: "Status",
    colPack: "Industry",
    colRegion: "Region",
    colCreated: "Created",
    sandbox: "Sandbox",
    emptyTitle: "No tenants yet",
    emptyBody: "Create the first one to give a customer their workspace.",
    emptyBodyViewer: "A platform admin creates tenants.",
    noMatchTitle: "Nothing matches",
    noMatchBody: "Try another name or status.",
    clearFilters: "Clear filters",
    page: "Page {{page}}",
    prev: "Previous page",
    next: "Next page",
  },

  tenant: {
    back: "Tenants",
    loading: "Loading tenant…",
    notFoundTitle: "No such tenant",
    notFoundBody: "It may have been typed wrong. Go back to the list.",
    overview: "Overview",
    subdomain: "Web address",
    openSite: "Open",
    pack: "Industry",
    region: "Region",
    locale: "Language",
    currency: "Currency",
    timezone: "Time zone",
    created: "Created",
    adminEmail: "Admin",
    resendInvite: "Resend invite",
    resending: "Sending…",
    resent: "Invite sent to {{email}}.",
    resendFailed: "Could not send the invite.",
    suspension: "Suspension",
    suspendedFor: "Reason",
    suspendedMode: "Mode",
    suspendedAt: "Since",
    graceEnds: "Read-only until",
    setup: "Setup",
    attempts: "Attempts",
    lastError: "Last error",
    retry: "Retry setup",
    retrying: "Retrying…",
    retryFailed: "Could not restart setup.",
    suspend: "Suspend",
    restore: "Restore",
    history: "History",
    historyEmpty: "Nothing has happened yet.",
    historyMore: "Show older",
    createdEvent: "Created",
    historyFailed: "Could not load the history.",
    by: "by {{who}}",
    automatic: "automatically",
    byStaff: "platform staff",
    refusedRole: "Your role can't do this.",
    refusedScope: "Sandbox staff can't change a tenant outside the sandbox.",
    sandboxOnly: "Sandbox staff can view this tenant but not change it.",
    changedFirst: "Someone changed this tenant first. This is where it stands now.",
  },

  suspend: {
    title: "Suspend {{name}}",
    reason: "Reason",
    mode: "What happens",
    note: "Note (optional)",
    confirm: "Suspend",
    working: "Suspending…",
    cancel: "Cancel",
    close: "Close",
    failed: "Could not suspend the tenant.",
  },

  restore: {
    title: "Restore {{name}}",
    body: "Users can sign in and work again straight away.",
    note: "Note (optional)",
    confirm: "Restore",
    working: "Restoring…",
    cancel: "Cancel",
    close: "Close",
    failed: "Could not restore the tenant.",
  },

  create: {
    title: "New tenant",
    stepLabel: "Step {{n}} of {{total}}",
    stepIdentity: "Name",
    stepPack: "Industry",
    stepDefaults: "Defaults",
    stepAdmin: "Admin",
    name: "Company name",
    namePlaceholder: "KazKabel Group",
    subdomain: "Subdomain",
    subdomainHint: "Lowercase letters, numbers and hyphens.",
    checking: "Checking…",
    available: "{{host}} is free.",
    taken: "Already taken.",
    reserved: "Reserved. Pick another name.",
    invalid:
      "Use 3 to 63 lowercase letters, numbers or hyphens, not starting or ending with a hyphen.",
    checkFailed: "Could not check this name. You can still try to create it.",
    pack: "Industry pack",
    packsLoading: "Loading packs…",
    packsFailed: "Could not load the packs.",
    region: "Data region",
    locale: "Default language",
    currency: "Currency",
    currencyHint: "It can't be changed once money is recorded.",
    timezone: "Time zone",
    adminEmail: "Admin email",
    adminEmailHint: "They get an invite to set up the workspace.",
    emailInvalid: "Enter a full email address.",
    review: "Check and create",
    back: "Back",
    next: "Next",
    submit: "Create tenant",
    creating: "Creating…",
    cancel: "Cancel",
    failed: "Could not create the tenant.",
    noAnswer: "No answer came back. Try again: it won't make a second tenant.",
    refusedRole: "Your role can't create tenants.",
    takenOnSubmit: "That subdomain was taken a moment ago. Pick another.",
    reservedOnSubmit: "That subdomain is reserved. Pick another.",
    forbiddenTitle: "Only a platform admin can create tenants.",
    forbiddenBody: "Ask an admin, or go back to the list.",
    progressTitle: "Setting up {{name}}",
    progressLive: "{{name}} is live.",
    progressFailed: "Setup stopped.",
    openTenant: "Open {{host}}",
    viewTenant: "View tenant",
  },

  roles: {
    platform_admin: "Platform admin",
    platform_support: "Platform support",
    platform_billing: "Platform billing",
    platform_readonly: "Platform read-only",
  },

  // M15-8 and M15-9 (D-620 to D-623): S10, S17, S18 and the S19 banner.
  ...adminOpsEn,
  ...adminAgentsEn,
} as const;

export type AdminDict = Translated<typeof adminEn>;

export const adminRu: AdminDict = adminRuDict;
